import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { api, setLogoutUserCallback, setRefreshTokenCallback } from '../api';

export interface User {
  id: number;
  email: string;
  username: string;
  role: 'USER' | 'ADMIN';
  name?: string | null;
  googleId?: string | null;
  twitchId?: string | null;
  kickId?: string | null; // Add kickId
  profileImageUrl?: string | null;
  firstLoginProvider?: string | null;
}

type LoginSuccessCallback = (user: User) => void;

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  login: (identifier: string, password: string, turnstileToken?: string, isAdminLogin?: boolean) => Promise<any>;
  register: (email: string, username: string, password: string, name?: string, turnstileToken?: string) => Promise<any>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  processOAuthLogin: () => Promise<void>;
  signInWithKick: () => Promise<void>; // Add signInWithKick
  registerOnLoginSuccess: (callback: LoginSuccessCallback) => void;
  unregisterOnLoginSuccess: (callback: LoginSuccessCallback) => void;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  socket: Socket | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// PKCE Helper functions
const generateRandomString = (length: number): string => {
  const array = new Uint32Array(length / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

const base64urlencode = (a: ArrayBuffer): string => {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(a) as any))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const hashed = await sha256(verifier);
  return base64urlencode(hashed);
};


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [hasLoggedOut, setHasLoggedOut] = useState(false); // Track if user has explicitly logged out
  const [onLoginSuccess, setOnLoginSuccess] = useState<LoginSuccessCallback[]>([]);

  const registerOnLoginSuccess = useCallback((callback: LoginSuccessCallback) => {
    setOnLoginSuccess(prev => [...prev, callback]);
  }, []);

  const unregisterOnLoginSuccess = useCallback((callback: LoginSuccessCallback) => {
    setOnLoginSuccess(prev => prev.filter(cb => cb !== callback));
  }, []);

  const logout = useCallback(async (isUserInitiated: boolean = true) => {
    if (isLoggingOut) return;
    // console.log('[AuthContext] Initiating logout.', isUserInitiated ? '(User initiated)' : '(System initiated)');
    setIsLoggingOut(true);
    
    // Mark as explicitly logged out if user initiated
    if (isUserInitiated) {
      setHasLoggedOut(true);
    }
    
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('Logout API call failed, clearing state anyway.', e);
    } finally {
      setUser(null);
      setToken(null);
      // console.log('[AuthContext] User state cleared.');
      setIsLoggingOut(false);
    }
  }, [isLoggingOut]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (isLoggingOut) {
      // console.log('[AuthContext] Logout in progress, aborting token refresh.');
      return null;
    }
    
    // Don't try to refresh if user has explicitly logged out
    if (hasLoggedOut) {
      // console.log('[AuthContext] User has explicitly logged out, aborting token refresh.');
      return null;
    }
    
    // console.log('[AuthContext] Attempting to refresh token...');
    // NEW: Log cookies before refresh token call
    // console.log('[AuthContext] Cookies before refresh:', document.cookie);
    try {
      // The request interceptor in api.ts automatically handles adding the CSRF token.
      // Manually adding it here is redundant and can cause issues.
      const response = await api.post('/auth/refresh-token', {});

      // Backend artık token'ı body'de göndermiyor, HttpOnly cookie olarak set ediyor.
      // Bu yüzden sadece işlemin başarılı olup olmadığını kontrol etmemiz yeterli.
      if (response.data.success) {
        // console.log('[AuthContext] refreshToken successful. Backend has set new cookies.');
        
        // Yeni cookie'lerle kullanıcı bilgilerini tekrar çekerek state'i güncelleyelim.
        await checkAuth();

        // api.ts'deki interceptor'a işlemin başarılı olduğunu bildirmek için null olmayan bir değer döndür.
        // Bu değerin kendisi kullanılmayacak, sadece başarılı olduğunu belirtmek için.
        return "refreshed";
      }

      console.warn('[AuthContext] refreshToken call failed. Response:', response.data);
      logout(false); // System initiated logout
      return null;
    } catch (error) {
      console.error('[AuthContext] Error during refreshToken call. Logging out.', error);
      setToken(null);
      logout(false); // System initiated logout
      return null;
    }
  }, [logout, isLoggingOut, hasLoggedOut]);

  const checkAuth = useCallback(async () => {
    // Don't check auth if user has explicitly logged out
    if (hasLoggedOut) {
      // console.log('[AuthContext] User has explicitly logged out, skipping auth check.');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    // console.log('[AuthContext] checkAuth called. Attempting to fetch /auth/me.');
    // NEW: Log cookies before checkAuth call
    // console.log('[AuthContext] Cookies before checkAuth:', document.cookie);
    
    // HttpOnly cookies cannot be read from JavaScript - this is a security feature
    // The browser will automatically send them with requests
    // We should always try to call /auth/me and let the backend decide if the user is authenticated
    
    try {
      const response = await api.get('/auth/me');
      if (response.data.success && response.data.user) {
        const backendUser = response.data.user;
        const fullUser: User = {
          id: backendUser.userId,
          email: backendUser.email,
          username: backendUser.username,
          role: backendUser.role,
          name: backendUser.name,
          googleId: backendUser.googleId,
          twitchId: backendUser.twitchId,
          kickId: backendUser.kickId, // Add kickId
          profileImageUrl: backendUser.profileImageUrl,
          firstLoginProvider: backendUser.firstLoginProvider,
        };
        setUser(fullUser);
        setHasLoggedOut(false); // Reset logout flag on successful auth check
        // console.log('[AuthContext] checkAuth successful. User set:', fullUser.username);
        
        // Check if user should be redirected back to clip editor (for OAuth flows)
        const clipeditorReturnUrl = localStorage.getItem('clipeditor_return_url');
        if (clipeditorReturnUrl) {
          localStorage.removeItem('clipeditor_return_url');
          console.log('[AuthContext] Redirecting back to clip editor after OAuth:', clipeditorReturnUrl);
          // Small delay to ensure auth state is properly set
          setTimeout(() => {
            window.location.href = clipeditorReturnUrl;
          }, 500);
        }
      } else {
        setUser(null);
        // console.log('[AuthContext] checkAuth: /auth/me returned success:false or no user. User state cleared.');
      }
    } catch (error) {
      setUser(null);
      console.error('[AuthContext] checkAuth failed (API error). User state cleared.', error);
    } finally {
      setIsLoading(false);
      console.log('[AuthContext] checkAuth finished. isLoading set to false.');
    }
  }, [setUser, setIsLoading, hasLoggedOut]);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (identifier: string, password: string, turnstileToken?: string, isAdminLogin?: boolean) => {
    setError(null);
    setIsLoading(true);
    try {
      // Ensure we have a fresh CSRF token before login
      await api.get('/auth/csrf-token');
      
      const payload: any = { identifier, password };
      if (turnstileToken) {
        payload.cfTurnstileResponse = turnstileToken;
      }
      const loginUrl = isAdminLogin ? '/auth/admin/login' : '/auth/login';
      const response = await api.post(loginUrl, payload);
      
      if (response.data.success && response.data.user) {
        const backendUser = response.data.user;
        const fullUser: User = {
          id: backendUser.id,
          email: backendUser.email,
          username: backendUser.username,
          role: backendUser.role,
          name: backendUser.name,
          googleId: backendUser.googleId,
          twitchId: backendUser.twitchId,
          kickId: backendUser.kickId, // Add kickId
          profileImageUrl: backendUser.profileImageUrl,
          firstLoginProvider: backendUser.firstLoginProvider,
        };
        setUser(fullUser);
        setHasLoggedOut(false); // Reset logout flag on successful login
        onLoginSuccess.forEach(cb => cb(fullUser));
        
        // Check if user should be redirected back to clip editor
        const clipeditorReturnUrl = localStorage.getItem('clipeditor_return_url');
        if (clipeditorReturnUrl) {
          localStorage.removeItem('clipeditor_return_url');
          console.log('[AuthContext] Redirecting back to clip editor:', clipeditorReturnUrl);
          // Small delay to ensure login state is properly set
          setTimeout(() => {
            window.location.href = clipeditorReturnUrl;
          }, 500);
        }
      }
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, username: string, password: string, name?: string, turnstileToken?: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const payload: any = { email, username, password, name };
      if (turnstileToken) {
        payload.cfTurnstileResponse = turnstileToken;
      }
      const response = await api.post('/auth/register', payload);
      if (response.data.success) {
        await login(email, password);
      }
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred during registration.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const processOAuthLogin = async () => {
    setHasLoggedOut(false); // Reset logout flag before OAuth login
    await checkAuth();
  };

  const signInWithKick = async () => {
    // This function is now handled in LoginPage.tsx to have access to turnstile token
    // We keep it here as a placeholder or for future refactoring.
    console.error("signInWithKick should be called from a component with Turnstile access.");
  };

  const exchangeKickCode = async (code: string, verifier: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post('/auth/kick/exchange-code', {
        code,
        codeVerifier: verifier,
      });
      if (response.data.success && response.data.user) {
        const backendUser = response.data.user;
        const fullUser: User = {
          id: backendUser.id,
          email: backendUser.email,
          username: backendUser.username,
          role: backendUser.role,
          name: backendUser.name,
          googleId: backendUser.googleId,
          twitchId: backendUser.twitchId,
          kickId: backendUser.kickId,
          profileImageUrl: backendUser.profileImageUrl,
          firstLoginProvider: backendUser.firstLoginProvider,
        };
        setUser(fullUser);
        setHasLoggedOut(false); // Reset logout flag on successful OAuth login
        onLoginSuccess.forEach(cb => cb(fullUser));
      }
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred during Kick login.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLogoutUserCallback(logout);
    setRefreshTokenCallback(refreshToken);
  }, [logout, refreshToken]);

  useEffect(() => {
    // Connect socket when user logs in, disconnect when they log out.
    if (user && !socket) {
      const isDev = import.meta.env.DEV;
      const socketUrl = isDev 
        ? (import.meta.env['VITE_SOCKET_URL'] || 'http://localhost:4001')
        : (import.meta.env['VITE_SOCKET_URL'] || '');
      // The 'auth' object with the token is no longer needed because authentication
      // is now handled via secure, httpOnly cookies automatically sent by the browser.
      // withCredentials: true ensures that the browser sends the cookies with the request.
      const newSocket = io(socketUrl, {
        withCredentials: true
      });
      setSocket(newSocket);

      newSocket.on('connect', () => console.log('[SocketContext] Connected to Socket.IO server.'));
      newSocket.on('disconnect', () => console.log('[SocketContext] Disconnected from Socket.IO server.'));
      newSocket.on('connect_error', (err) => console.error('[SocketContext] Socket connection error:', err));

    } else if (!user && socket) {
      console.log('[AuthContext] User is null, disconnecting socket.');
      socket.disconnect();
      setSocket(null);
    }

    // Cleanup on component unmount
    return () => {
      if (socket) {
        console.log('[AuthContext] Disconnecting socket on cleanup.');
        socket.disconnect();
      }
    };
  }, [user, socket]); // Depend on user and socket state

  const value = {
    token,
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingOut,
    login,
    register,
    logout,
    checkAuth,
    processOAuthLogin,
    signInWithKick,
    exchangeKickCode,
    registerOnLoginSuccess,
    unregisterOnLoginSuccess,
    error,
    setError,
    setUser,
    socket,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};